import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CommuteIcon from '@mui/icons-material/Commute';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from '../../api/axiosInstance';

function formatHours(value) {
  return `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}h`;
}

function formatDelta(value) {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${formatHours(number)}`;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function formatCount(value) {
  return Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

function getArticleCycle(article = {}) {
  return article.serviceMetric === 'travel' ? 'annuel' : (article.billingCycle || 'oneshot');
}

function getArticleLineTotal(article = {}) {
  return Number(article.quantity || 0) * Number(article.unitPrice || 0);
}

function getArticleMonthlyTotal(article = {}) {
  if (Number.isFinite(Number(article.monthlyTotal))) return Number(article.monthlyTotal);
  const lineTotal = getArticleLineTotal(article);
  return getArticleCycle(article) === 'annuel' ? lineTotal / 12 : lineTotal;
}

function getArticleAnnualTotal(article = {}) {
  if (Number.isFinite(Number(article.annualTotal))) return Number(article.annualTotal);
  const lineTotal = getArticleLineTotal(article);
  return getArticleCycle(article) === 'mensuel' ? lineTotal * 12 : lineTotal;
}

function getContractArticlesMonthlyTotal(contract) {
  return (contract?.articles || []).reduce((sum, article) => sum + getArticleMonthlyTotal(article), 0);
}

function getContractArticlesAnnualTotal(contract) {
  return (contract?.articles || []).reduce((sum, article) => sum + getArticleAnnualTotal(article), 0);
}

function metricLabel(metric) {
  return {
    admin_hours: 'Heures admin',
    engineer_hours: 'Heures ingé',
    admin_days: 'Jours admin',
    engineer_days: 'Jours ingé',
    pilotage_days: 'Jours pilotage',
    travel: 'Déplacement',
  }[metric] || metric || 'Article';
}


const INFOGERANCE_METRICS = new Set(['admin_hours', 'engineer_hours', 'admin_days', 'engineer_days', 'pilotage_days', 'travel']);

function normalizeText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function isInfogeranceArticle(article = {}) {
  if (INFOGERANCE_METRICS.has(article.serviceMetric)) return true;
  const text = normalizeText([
    article.productName,
    article.productCode,
    article.category,
    article.subCategory,
  ].filter(Boolean).join(' '));
  return text.includes('infogerance');
}

function getArticleSectionTotals(articles = []) {
  return articles.reduce((acc, article) => {
    acc.monthly += getArticleMonthlyTotal(article);
    acc.annual += getArticleAnnualTotal(article);
    return acc;
  }, { monthly: 0, annual: 0 });
}

function ContractArticlesSection({ title, articles }) {
  const totals = getArticleSectionTotals(articles);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ p: 1.75 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{articles.length} article(s)</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" variant="outlined" label={`${formatCurrency(totals.monthly)} / mois`} />
          <Chip size="small" variant="outlined" label={`${formatCurrency(totals.annual)} / an`} />
        </Stack>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Article</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Cycle</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Qté</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Prix unit.</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Total mensuel</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Total annuel</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.map((article, index) => {
              const quantity = Number(article.quantity || 0);
              const unitPrice = Number(article.unitPrice || 0);
              const monthlyTotal = getArticleMonthlyTotal(article);
              const annualTotal = getArticleAnnualTotal(article);
              return (
                <TableRow key={article._id || `${title}-${index}`} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{article.productName || '-'}</Typography>
                    {(article.productCode || article.category || article.subCategory) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {[article.productCode, article.category, article.subCategory].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{metricLabel(article.serviceMetric)}</TableCell>
                  <TableCell>{getArticleCycle(article)}</TableCell>
                  <TableCell align="right">{quantity.toLocaleString('fr-FR')}</TableCell>
                  <TableCell align="right">{formatCurrency(unitPrice)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(monthlyTotal)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(annualTotal)}</TableCell>
                </TableRow>
              );
            })}
            {!articles.length && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>Aucun article.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function ContractMetricCard({ label, helper, icon, data, metricKey, color }) {
  const used = Number(data?.used?.[metricKey] || 0);
  const included = Number(data?.included?.[metricKey] || 0);
  const delta = Number(data?.delta?.[metricKey] || 0);
  const remaining = Math.max(0, included - used);
  const overrun = delta > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        borderRadius: 2,
        height: '100%',
        borderColor: overrun ? 'warning.main' : 'divider',
        bgcolor: overrun ? 'rgba(245, 158, 11, 0.06)' : 'background.paper',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color,
                bgcolor: `${color}14`,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{label}</Typography>
              <Typography variant="caption" color="text.secondary">{helper}</Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            color={overrun ? 'warning' : 'success'}
            label={overrun ? `${formatHours(delta)} à régul` : `${formatHours(remaining)} dispo`}
          />
        </Stack>

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            {formatHours(used)}
            <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}> / {formatHours(included)}</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {formatHours(used)} consommé / {formatHours(included)} prévu ({formatDelta(delta)})
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function TravelMetricCard({ data }) {
  const used = Number(data?.used?.travel || 0);
  const included = Number(data?.included?.travel || 0);
  const delta = Number(data?.delta?.travel || 0);
  const remaining = Math.max(0, included - used);
  const overrun = delta > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        borderRadius: 2,
        height: '100%',
        borderColor: overrun ? 'warning.main' : 'divider',
        bgcolor: overrun ? 'rgba(245, 158, 11, 0.06)' : 'background.paper',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', bgcolor: '#f59e0b14' }}>
              <CommuteIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Déplacements</Typography>
              <Typography variant="caption" color="text.secondary">Déplacements Helpdesk infogérance</Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            color={overrun ? 'warning' : 'success'}
            label={overrun ? `${formatCount(delta)} à régul` : `${formatCount(remaining)} dispo`}
          />
        </Stack>

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            {formatCount(used)}
            <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}> / {formatCount(included)}</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {formatCount(used)} consommé / {formatCount(included)} prévu ({formatCount(delta)})
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ClientContracts() {
  const [data, setData] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedPeriod, setSelectedPeriod] = useState('year');
  const [selectedContractId, setSelectedContractId] = useState('all');
  const [viewingContract, setViewingContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`/api/client/dashboard?year=${selectedYear}&view=contracts`)
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setError('');
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || 'Impossible de charger les contrats.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [selectedYear]);

  const regularization = data?.regularization;
  const contracts = regularization?.byContract || [];
  const selectedContract = contracts.find((contract) => String(contract._id) === String(selectedContractId));
  const periodData = selectedContract || regularization;
  const activePeriodKey = selectedPeriod === 'month' ? 'month' : 'year';
  const activePeriodLabel = selectedPeriod === 'month' ? 'Mois en cours' : `Année ${regularization?.yearLabel || selectedYear}`;
  const activeIncludedKey = selectedPeriod === 'month' ? 'monthIncluded' : 'yearIncluded';
  const activeDeltaKey = selectedPeriod === 'month' ? 'month' : 'year';
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
  const rows = useMemo(() => (selectedContract ? [selectedContract] : contracts), [selectedContract, contracts]);

  if (loading) {
    return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack gap={3} >
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
        <Box>
          <Typography variant="overline" sx={{ color: 'success.dark', fontWeight: 800 }}>{data?.organization?.name || 'Client'}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>Vos contrats</Typography>
          <Typography color="text.secondary">Détail des forfaits infogérance et des temps consommés.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="client-contract-period-label">Période</InputLabel>
            <Select
              labelId="client-contract-period-label"
              value={selectedPeriod}
              label="Période"
              onChange={(event) => setSelectedPeriod(event.target.value)}
            >
              <MenuItem value="year">Année sélectionnée</MenuItem>
              <MenuItem value="month">Mois en cours</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="client-contract-year-label">Année</InputLabel>
            <Select
              labelId="client-contract-year-label"
              value={selectedYear}
              label="Année"
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="client-contract-filter-label">Contrat</InputLabel>
            <Select
              labelId="client-contract-filter-label"
              value={selectedContractId}
              label="Contrat"
              onChange={(event) => setSelectedContractId(event.target.value)}
            >
              <MenuItem value="all">Tous les contrats</MenuItem>
              {contracts.map((contract) => (
                <MenuItem key={contract._id} value={contract._id}>{contract.name || contract.reference || 'Contrat'}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>{activePeriodLabel}</Typography>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 3 }}>
            <ContractMetricCard
              label="Admin"
              helper="Heures administrateur prévues"
              icon={<AdminPanelSettingsIcon />}
              data={periodData?.[activePeriodKey]}
              metricKey="admin"
              color="#0f766e"
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <ContractMetricCard
              label="Ingé"
              helper="Heures ingénieur prévues"
              icon={<EngineeringIcon />}
              data={periodData?.[activePeriodKey]}
              metricKey="engineer"
              color="#2563eb"
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <ContractMetricCard
              label="Pilotage"
              helper="Heures pilotage prévues"
              icon={<ManageAccountsIcon />}
              data={periodData?.[activePeriodKey]}
              metricKey="pilotage"
              color="#7c3aed"
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <TravelMetricCard data={periodData?.[activePeriodKey]} />
          </Grid>
        </Grid>
      </Box>

      <Paper variant="outlined" sx={{ p: 2.25, mt: 4, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Contrats de service</Typography>
            <Typography variant="body2" color="text.secondary">Liste des contrats actifs et détails associés.</Typography>
          </Box>
          <Stack spacing={1.25}>
            {rows.map((contract) => {
              const monthlyTotal = getContractArticlesMonthlyTotal(contract);
              const annualTotal = getContractArticlesAnnualTotal(contract);
              return (
                <Paper key={contract._id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{contract.name || contract.reference || 'Contrat'}</Typography>
                      <Typography variant="caption" color="text.secondary">{contract.reference || (contract.hasArticleHours ? 'Articles contrat' : 'Champs contrat')}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="flex-end">
                      <Chip size="small" color="primary" variant="outlined" label={`${formatCurrency(monthlyTotal)} / mois`} />
                      <Chip size="small" variant="outlined" label={`${formatCurrency(annualTotal)} / an`} />
                      <Tooltip title="Voir le contrat" arrow>
                        <IconButton size="small" color="primary" onClick={() => setViewingContract(contract)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Téléchargement bientôt disponible" arrow>
                        <span>
                          <IconButton size="small" disabled>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
            {!rows.length && (
              <Typography variant="body2" color="text.secondary" align="center">Aucun contrat actif.</Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={!!viewingContract} onClose={() => setViewingContract(null)} fullWidth maxWidth="md">
        <DialogTitle>{`Contrat - ${viewingContract?.name || viewingContract?.reference || ''}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={viewingContract?.reference || 'Sans référence'} />
              <Chip label={`${formatCurrency(getContractArticlesMonthlyTotal(viewingContract))} / mois`} color="primary" variant="outlined" />
              <Chip label={`${formatCurrency(getContractArticlesAnnualTotal(viewingContract))} / an`} variant="outlined" />
              <Chip label={viewingContract?.hasArticleHours ? 'Articles contrat' : 'Champs contrat'} />
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Admin inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatHours(viewingContract?.[activeIncludedKey]?.admin)}</Typography>
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Ingé inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatHours(viewingContract?.[activeIncludedKey]?.engineer)}</Typography>
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Pilotage inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatHours(viewingContract?.[activeIncludedKey]?.pilotage)}</Typography>
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Déplacements inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{Number(viewingContract?.[activeIncludedKey]?.travel || 0).toLocaleString('fr-FR')}</Typography>
                </Paper>
              </Grid>
            </Grid>
            {(() => {
              const articles = viewingContract?.articles || [];
              const infogeranceArticles = articles.filter(isInfogeranceArticle);
              const otherArticles = articles.filter((article) => !isInfogeranceArticle(article));
              return (
                <Stack spacing={2}>
                  <ContractArticlesSection title="Infogérance" articles={infogeranceArticles} />
                  <ContractArticlesSection title="Autres articles" articles={otherArticles} />
                </Stack>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingContract(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Suivi infogérance par contrat</Typography>
            <Typography variant="body2" color="text.secondary">Seuls les temps Helpdesk/Infogérance consomment le forfait prévu au contrat sur la période sélectionnée.</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Contrat</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Admin</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Ingé</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Pilotage</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{row.name || row.reference || 'Contrat'}</TableCell>
                    <TableCell align="right">{formatDelta(row[activeDeltaKey]?.delta?.admin)}</TableCell>
                    <TableCell align="right">{formatDelta(row[activeDeltaKey]?.delta?.engineer)}</TableCell>
                    <TableCell align="right">{formatDelta(row[activeDeltaKey]?.delta?.pilotage)}</TableCell>
                    <TableCell>{row.hasArticleHours ? 'Articles contrat' : 'Champs contrat'}</TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>Aucun contrat actif.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Stack>
  );
}
